import React, { useState, useEffect } from 'react';
import { User, Bot, ImageIcon, Loader2 } from 'lucide-react';
import { SUPABASE_URL, SUPABASE_SERVICE_ROLE } from '../config';

interface Message {
  sender: string;
  message: string;
  id: string;
  response_metadata?: string | null;
}

interface ChatMessageProps {
  message: Message;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAI = message.sender === 'ai';
  
  useEffect(() => {
    const fetchImage = async () => {
      if (!message.response_metadata || message.response_metadata === 'null') return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch the image using service role credentials
        const response = await fetch(message.response_metadata, {
          headers: {
            'apikey': SUPABASE_SERVICE_ROLE,
            'Authorization': `Bearer ${SUPABASE_SERVICE_ROLE}`
          }
        });

        if (!response.ok) {
          throw new Error(`Error de servidor: ${response.status} ${response.statusText}`);
        }

        // Convert the binary data to a blob
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
      } catch (err) {
        console.error('Error fetching image:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar la imagen');
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    // Cleanup function to revoke object URL
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [message.response_metadata]);

  return (
    <div className={`flex gap-4 p-6 ${isAI ? 'bg-white' : ''} animate-fade-in`}>
      <div className="flex-shrink-0">
        {isAI ? (
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium">{isAI ? 'IA' : 'Tú'}</span>
        </div>
        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{message.message}</p>
        
        {/* Image Section */}
        {message.response_metadata && message.response_metadata !== 'null' && (
          <div className="mt-4">
            {loading ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Cargando imagen...</span>
              </div>
            ) : error ? (
              <div className="flex items-center gap-2 text-red-500">
                <ImageIcon className="w-5 h-5" />
                <span>{error}</span>
              </div>
            ) : imageUrl && (
              <img
                src={imageUrl}
                alt="AI Generated Visualization"
                className="max-w-full rounded-lg shadow-md"
                style={{ maxHeight: '400px' }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};